import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import { FaLink } from 'react-icons/fa';
import { toast } from '../../toasts';
import esignatureService from '../../services/esignature.service';
import { Button, DataTable, Pill } from '../EntityList';
import { getEsignatureStatusTone } from '../../constants/esignatureStatus';
import { useLabels } from '../../hooks/useLabels';
import { getError } from '../../utils';

/**
 * Recovery path for submissions created directly in DocuSeal's own UI
 * (e.g. an admin clicked DocuSeal's own Send button instead of returning to
 * the "Enviar a firmar" wizard). Lists recent DocuSeal submissions so the
 * admin can pick the right one and link it back onto this document.
 */
export default function LinkExistingSignatureModal({ show, onHide, document, objectType, objectId, onLinked }) {
	const { t } = useLabels();
	const [loading, setLoading] = useState(false);
	const [submissions, setSubmissions] = useState([]);
	const [linkingId, setLinkingId] = useState(null);

	useEffect(() => {
		if (!show) return;
		const load = async () => {
			setLoading(true);
			try {
				const { data } = await esignatureService.listSubmissions();
				const list = Array.isArray(data) ? data : data?.data || [];
				setSubmissions(list);
			} catch (err) {
				toast.error(getError(err));
			}
			setLoading(false);
		};
		load();
	}, [show]);

	const handleLink = async (submissionId) => {
		setLinkingId(submissionId);
		try {
			await esignatureService.linkSubmission(document._id, {
				submissionId,
				entityType: objectType,
				entityId: objectId,
			});
			toast.success(t('esignature.link.linked'));
			onLinked?.();
			onHide();
		} catch (err) {
			toast.error(getError(err));
		}
		setLinkingId(null);
	};

	const columns = [
		{
			key: 'template',
			header: t('esignature.link.template'),
			render: (row) => row.template?.name || row.id,
		},
		{
			key: 'signers',
			header: t('esignature.link.signers'),
			render: (row) => (row.submitters || []).map((s) => s.email).join(', '),
		},
		{
			key: 'status',
			header: t('esignature.link.statusHeader'),
			render: (row) => <Pill tone={getEsignatureStatusTone(row.status)}>{row.status}</Pill>,
		},
		{
			key: 'action',
			header: '',
			className: 'tw:text-right',
			render: (row) => (
				<Button size='sm' onClick={() => handleLink(row.id)} disabled={linkingId === row.id}>
					{linkingId === row.id
						? <Spinner animation='border' size='sm' />
						: t('esignature.link.linkButton')}
				</Button>
			),
		},
	];

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			animation={false}
			dialogClassName='tw:max-w-[720px]'
			contentClassName='entity-list-page el-modal tw:overflow-hidden tw:rounded-[14px] tw:border-0 tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0 tw:shadow-overlay'
		>
			<Modal.Header
				closeButton
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaLink />
					</span>
					<div>
						<Modal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							{t('esignature.link.title')}
						</Modal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
							{t('esignature.link.description')}
						</p>
					</div>
				</div>
			</Modal.Header>
			<Modal.Body className='tw:px-[22px] tw:py-[18px]'>
				<div className='tw:overflow-hidden tw:rounded-panel tw:border tw:border-border'>
					<DataTable
						bare
						columns={columns}
						rows={submissions}
						loading={loading}
						rowKey={(row) => row.id}
						emptyMessage={t('esignature.link.empty')}
					/>
				</div>
			</Modal.Body>
			<Modal.Footer className='tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px]'>
				<Button variant='default' onClick={onHide}>{t('action.cancel')}</Button>
			</Modal.Footer>
		</Modal>
	);
}
